/** lag-datetime {
 *      value:
 *          Stored as Date object, if the value is provided as a string it is parsed as UTC timezone,
 *          though it will be converted to local timezone since Date objects are always stored in local timezone.
 *          Accepted formats are anything parsable by Date constructor (ISO date-time format string).
 *      display:
 *          What to display (when not in countdown mode).
 *          Valid values are 'date' or 'time' or null, where null shows both date and time together and the other options restrict to their stated part.
 *      countdownWithin:
 *          Within what period to show a countdown rather than a date-time output.
 *          Stored as milliseconds. May be specified as a number in milliseconds or a time period in hh:mm format,
 *          or true/false where true means always and false means never.
 *      countdownDoneText:
 *          A string (or null) that will be displayed when the coundown reaches zero.
 *          This is only used when a countdown is performed, ie if countdownWithin is false then countdownDoneText will never display.
 *  }
 *
 *  As is normal for polymer elements, any of these properties can be specified upon the element direclty as attributes.
 *  Remember that properties with capital letters must be adjusted when specified as attributes, where capitals are lower-cased and
 *  preceeded by a dash. Eg: 'fooBar' -> 'foo-bar'.
 *
 *  <lag-datetime value="2015-06-25T14:37" display="time" countdown-within="12:00" countdown-done-text="No more time left!"></lag-datetime>
 *
 *  OUTPUT TIMEZONE:
 *  Output date-time will be in the user's local timezone (or another timezone the user has selected as display timezone).
 *
 *  INPUT TIMEZONE:
 *  Input date-time is assumed to be UTC unless otherwise specified; examples...
 *  1) lagDatetime.value = '2015-06-25'; // As no timezone is specifid it will be assumed as UTC time.
 *      output> '2015-06-24 16:00'; // User's local/output timezone: UTC-8 (input was in UTC timezone so output is adjusted to user's timezone).
 *  2) lagDatetime.value = new Date('2015-06-25'); // Creating a Date object will take on the local timezone by default.
 *      output> '2015-06-25 00:00'; // User's local/output timezone: UTC-8 (input was already in user's timezone, so output is the same as input).
 *  3) lagDatetime.value = '2015-06-25T00:00-08'; // Timezone specified UTC-8 in ISO format (https://en.wikipedia.org/wiki/ISO_8601#Time_zone_designators).
 *      output> '2015-06-25 00:00'; // User's local/output timezone: UTC-8 (input was already in user's timezone, so output is the same as input).
 */

// TODO: allow user to change their timezone (global setting, also save/load between sessions) and all lag-datetime elements would auto update

import { PolymerElement } from '@polymer/polymer/polymer-element.js';

import * as tools from '../../utility/tools.js';
import { defineElement } from '../../utility/defineElement.js';
import { debounce } from '../../utility/debounce.js';

import { template } from './lag-datetime.html.js';

defineElement(class extends PolymerElement {

    constructor() {
        super();

        this._COUNTDOWN_REFRESH_FREQ = 500; // ms
    }

    static get is() { return 'lag-datetime'; }

    static get template() { return template; }

    static get properties() {
        return {
            value: {
                type: Date, // Date object (also accepts a date/time string assumed to be in UTC timezone which will be parsed into a Date object).
                value: null,
                reflectToAttribute: true,
                observer: '_valueChanged',
                notify: true
            },

            display: {
                type: String, // Can be 'date' or 'time', otherwise both.
                value: null, // Default to null (ie show full date & time).
                reflectToAttribute: true,
                observer: '_displayChanged'
            },

            countdownWithin: {
                type: Number, // Milliseconds. A string can be specified with a number of milliseconds or hh:mm format and will be parsed accordingly, or true/false (show:always/never).
                value: null, // Defaults to null (ie never display countdown).
                reflectToAttribute: true,
                observer: '_countdownWithinChanged'
            },

            countdownDoneText: {
                type: String,
                value: null,
                reflectToAttribute: true,
                observer: '_countdownDoneTextChanged'
            },

            _countdownTimer: {
                type: Number,
                value: 0
            },

            _displayValue: {
                type: String,
            },
            _displayCountdown: {
                type: String
            }
        };
    }

    _valueChanged(newValue, oldValue) {
        if (newValue === oldValue) return;

        if (!newValue) {
            this.value = null;
        }
        else if (newValue instanceof Date) {
            this.value = newValue;
        }
        else {
            this.value = tools.date.fromUTC(newValue); // Convert to Date object, and assume it is UTC timezone.
        }

        this._update();
    }

    _displayChanged(newDisplay, oldDisplay) {
        if (newDisplay === oldDisplay) return;

        if (newDisplay !== 'date' && newDisplay !== 'time') {
            this.display = null; // null/default : show both date and time
        }
        // else it's a valid value.

        this._update();
    }

    _countdownWithinChanged(newCountdown, oldCountdown) {
        if (newCountdown === oldCountdown) return;

        if (!newCountdown || newCountdown === 'false') {
            this.countdownWithin = null;
        }
        else if (newCountdown === true || newCountdown === 'true') {
            this.countdownWithin = Number.MAX_SAFE_INTEGER;
        }
        else if (newCountdown instanceof Number) {
            this.countdownWithin = newCountdown;
        }
        else {
            const idxColon = newCountdown.indexOf(':');
            if (idxColon <= 0) throw new Error('Invalid Argument: countdownWithin: ' + newCountdown);

            let hours = newCountdown.substr(0, idxColon), minutes = newCountdown.substr(idxColon + 1);
            hours = parseInt(hours);
            minutes = parseInt(minutes);

            this.countdownWithin = ((hours * 60) + minutes) * 60 * 1000; // Convert to milliseconds.
        }

        this._update();
    }

    _countdownDoneTextChanged() {
        this._update();
    }

    _update() {
        //this._debouncer_update =
        //    debounce(this._debouncer_update, 100).then(() => {
                this._displayValue = this._computeOutput();
        //    });
    }

    _computeOutput() {
        // Must have a value, or just return blank.
        // Also countdownWithin must either be null or a duration, if not then we wait for it to be parsed in change handler.
        if (!this.value || !(this.value instanceof Date) || (this.countdownWithin && !(this.countdownWithin instanceof Number))) return '';

        // If a countdown timer was running then clear it. We will create one again if necessary.
        if (this._countdownTimer) {
            clearInterval(this._countdownTimer);
            this._displayCountdown = '';
        }

        // Countdown is enabled and active, show as countdown rather than static value.
        // If !countdownDoneText then isWithinDuration check will fail when after the time (ie no text provided so we just display date as normal when after).
        if (this.countdownWithin && this._isWithinDuration(this.countdownWithin, this.value, !this.countdownDoneText)) {
            // Create a interval timer to display/update the countdown.

            this._countdownTimer = setInterval(this._updateDisplayValueCountdown.bind(this), this._COUNTDOWN_REFRESH_FREQ); // Update interval less than a second.
            return '';
        }
        else {
            // Show the date-time as a normal (static) value (not a countdown).

            return this._formatLocalized(this.value, this.display || 'MMM Do YYYY, HH:mm');
        }
    }
    _formatLocalized(theValue, theFormat) {
        // TODO: provide a localization code (from Localizer) for localized formatting.
        // TODO: if user has set a specific/different display timezone (vs their system timezone) then use that instead.

        if (theValue instanceof Date) {
            return tools.date.format(theValue, theFormat);
        }
        else return null;
    }
    _updateDisplayValueCountdown() {
        // The value has passed, so stop countdown.
        if (!this.value || tools.date.isBefore(this.value, Date.now())) {
            clearInterval(this._countdownTimer);
            if (this.countdownDoneText)
                this._displayCountdown = this.countdownDoneText;
            else
                this._displayCountdown = '0';
            return;
        }
        // Otherwise update countdown...

        // Difference = now - value.
        this._displayCountdown = tools.date.distanceInWordsToNow(this.value);
    }
    _isWithinDuration(theDurationWithinMS, theDate, falseIfAfter) {
        // Threshold = now + duration
        // Eg: now = 12:00, duration = 1h, moment = 13:30, now + duration = 13:00; threshold not after, so not within duration.
        const now = Date.now(),
            threshold = tools.date.addMilliseconds(now, theDurationWithinMS),
            withinDuration = tools.date.isAfter(threshold, theDate),
            afterTime = tools.date.isAfter(now, theDate);

        // True if within duration and not after time (or we don't care about after time).
        return withinDuration && (!falseIfAfter || !afterTime);
    }

});